function newSlider(totalSlides, slidesPerGroup = 1, options = {}) {
  return {
    currentIndex: 0,
    totalSlides,
    slidesPerGroup,
    slidesPerView: 1,
    isDesktop: false,
    isTablet: false,
    isLoaded: false,
    // Default options
    desktopSlides: options.desktopSlides || 4,
    tabletSlides: options.tabletSlides || 2,

    init() {
      this.totalSlides = totalSlides;
      this.updateSlidesPerView();

      // Set loaded state after initialization - needed for thumbnails/mobile nav visibility
      this.$nextTick(() => {
        this.isLoaded = true;
      });

      window.addEventListener("resize", () => {
        this.updateSlidesPerView();
        // Ensure current index is valid after resize
        const maxIndex = Math.max(0, this.totalSlides - this.slidesPerGroup);
        this.currentIndex = Math.min(this.currentIndex, maxIndex);
      });
    },

    updateSlidesPerView() {
      this.isDesktop = window.matchMedia("(min-width: 1024px)").matches;
      this.isTablet = !this.isDesktop && window.matchMedia("(min-width: 768px)").matches;

      // Update slidesPerGroup based on breakpoint
      if (this.isDesktop) {
        this.slidesPerGroup = Math.min(this.desktopSlides, this.totalSlides);
      } else if (this.isTablet) {
        this.slidesPerGroup = Math.min(this.tabletSlides, this.totalSlides);
      } else {
        this.slidesPerGroup = 1;
      }

      this.slidesPerView = this.slidesPerGroup;
    },

    get activeGroupIndex() {
      return Math.floor(this.currentIndex / this.slidesPerGroup);
    },

    get totalGroups() {
      return Math.ceil((this.totalSlides - this.slidesPerView + 1) / this.slidesPerGroup);
    },

    get isAtStart() {
      return this.currentIndex === 0;
    },

    get isAtEnd() {
      return this.currentIndex >= this.totalSlides - this.slidesPerView;
    },

    updateCurrentSlide() {
      const slider = this.$refs && this.$refs.slider;
      if (!slider) return;

      const slides = slider.children;
      if (!slides.length) return;

      const firstSlide = Array.from(slides).find((slide) => window.getComputedStyle(slide).display !== "none");
      if (!firstSlide) return;

      const slideWidth = firstSlide.offsetWidth;
      if (slideWidth === 0) return;

      const scrollLeft = slider.scrollLeft;
      const gap = parseInt(window.getComputedStyle(slider).gap) || 0;
      const slideFullWidth = slideWidth + gap;

      // Calculate the current index based on scroll position and group size
      const rawIndex = Math.round(scrollLeft / slideFullWidth);
      const maxIndex = Math.max(0, this.totalSlides - this.slidesPerView);
      this.currentIndex = Math.min(rawIndex, maxIndex);

      // Update thumbnail scroll position
      this.$nextTick(() => {
        this.updateThumbnailScroll();
      });
    },

    prevSlide() {
      if (this.isAtStart) return;
      const newIndex = Math.max(0, this.currentIndex - this.slidesPerGroup);
      this.goToSlide(newIndex);
    },

    nextSlide() {
      if (this.isAtEnd) return;
      const newIndex = Math.min(this.totalSlides - this.slidesPerView, this.currentIndex + this.slidesPerGroup);
      this.goToSlide(newIndex);
    },

    goToGroup(groupIndex) {
      const boundedGroupIndex = Math.max(0, Math.min(groupIndex, this.totalGroups - 1));
      this.goToSlide(boundedGroupIndex * this.slidesPerGroup);
    },

    goToSlide(index = 0, isGroupNavigation = false) {
      const slider = this.$refs && this.$refs.slider;
      if (!slider) return;

      const slides = slider.children;
      if (!slides.length) return;

      const firstSlide = Array.from(slides).find((slide) => window.getComputedStyle(slide).display !== "none");
      if (!firstSlide) return;

      const slideWidth = firstSlide.offsetWidth;
      const gap = parseInt(window.getComputedStyle(slider).gap) || 0;

      if (isGroupNavigation) {
        const groupIndex = Math.max(0, Math.min(index, this.totalGroups - 1));
        this.currentIndex = groupIndex * this.slidesPerGroup;
      } else {
        // Ensure index is within bounds
        const maxIndex = Math.max(0, this.totalSlides - this.slidesPerView);
        this.currentIndex = Math.max(0, Math.min(index, maxIndex));
      }

      // Calculate scroll position based on current index
      const targetScroll = this.currentIndex * (slideWidth + gap);

      slider.scrollTo({
        left: targetScroll,
        behavior: "smooth"
      });
    },

    updateThumbnailScroll() {
      const thumbnailContainer = this.$refs.thumbnailContainer;
      const activeThumbnail = this.$refs[`thumbnail-${this.currentIndex}`];

      if (!thumbnailContainer || !activeThumbnail) return;

      const containerWidth = thumbnailContainer.offsetWidth;
      const thumbnailWidth = activeThumbnail.offsetWidth;
      const thumbnailLeft = activeThumbnail.offsetLeft;

      // Calculate the center position of the active thumbnail
      const thumbnailCenter = thumbnailLeft + thumbnailWidth / 2;
      const containerCenter = containerWidth / 2;
      const targetScrollLeft = thumbnailCenter - containerCenter;

      // Scroll to center the active thumbnail
      thumbnailContainer.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth"
      });
    },

    openZoomModal() {
      // Get all slides and their images
      const slider = this.$refs.slider;
      if (!slider) {
        return;
      }

      const slides = slider.children;
      if (!slides.length) {
        return;
      }

      // Collect all media from slides
      const allMedia = [];
      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        const zoomSrcElement = slide.querySelector("[data-zoom-src]");
        if (zoomSrcElement) {
          let zoomSrc = zoomSrcElement.getAttribute("data-zoom-src");
          const imgElement = zoomSrcElement.querySelector("img");

          if (zoomSrc) {
            // Ensure the URL has the correct CDN base path
            const fullUrl = zoomSrc.startsWith("http") ? zoomSrc : `//${window.location.host}/cdn/shop/${zoomSrc}`;
            allMedia.push({
              src: fullUrl,
              alt: imgElement?.alt || "",
              zoom_src: fullUrl,
              preview_image: { src: fullUrl },
              title: imgElement?.alt || ""
            });
          }
        }
      }

      if (allMedia.length === 0) {
        return;
      }

      // Dispatch event to open zoom modal
      this.$dispatch("open-zoom-modal", {
        currentIndex: this.currentIndex,
        allMedia: allMedia
      });
    }
  };
}

export default newSlider;
